import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { useSmartPolling } from '../hooks/useSmartPolling';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Global game-server kill-switch state, mirrored from the backend
 * `/server-control/status`. When `blocked` is true, every server-start action
 * across team + personal space must be disabled and show `message`. A site
 * admin toggles this from the Command Center → Game Servers tab.
 */
interface ServerBlockContextType {
    blocked: boolean;
    message: string;
    refresh: () => void;
    registerPollingConsumer: () => () => void;
}

const DEFAULT_MESSAGE =
    'Game server starts are temporarily disabled for maintenance. ' +
    "We're working hard to restore this as quickly as possible — thanks for your patience.";

const ServerBlockContext = createContext<ServerBlockContextType>({
    blocked: false,
    message: DEFAULT_MESSAGE,
    refresh: () => { },
    registerPollingConsumer: () => () => { },
});

export function ServerBlockProvider({ children }: { children: ReactNode }) {
    const [blocked, setBlocked] = useState(false);
    const [message, setMessage] = useState(DEFAULT_MESSAGE);
    const [pollingConsumers, setPollingConsumers] = useState(0);

    const checkStatus = useCallback(async (signal: AbortSignal) => {
        const { data } = await axios.get(`${API}/server-control/status`, {
            signal,
            timeout: 8000,
        });
        setBlocked(!!data?.blocked);
        if (data?.message) setMessage(data.message);
    }, []);

    const { refreshNow } = useSmartPolling(checkStatus, {
        enabled: pollingConsumers > 0,
        intervalMs: 30_000,
        jitterRatio: 0.1,
        maxBackoffMs: 120_000,
    });

    const registerPollingConsumer = useCallback(() => {
        let released = false;
        setPollingConsumers(current => current + 1);
        return () => {
            if (released) return;
            released = true;
            setPollingConsumers(current => Math.max(0, current - 1));
        };
    }, []);

    return (
        <ServerBlockContext.Provider value={{
            blocked,
            message,
            refresh: refreshNow,
            registerPollingConsumer,
        }}>
            {children}
        </ServerBlockContext.Provider>
    );
}

export function useServerBlockPolling(enabled = true) {
    const { registerPollingConsumer } = useContext(ServerBlockContext);
    useEffect(() => {
        if (!enabled) return;
        return registerPollingConsumer();
    }, [enabled, registerPollingConsumer]);
}

export function useServerBlock(pollingEnabled = true) {
    const context = useContext(ServerBlockContext);
    useServerBlockPolling(pollingEnabled);
    return context;
}

/**
 * Reusable maintenance notice for server-start modals. Renders nothing when
 * starts are allowed, so it can be dropped unconditionally into any modal.
 */
export function ServerBlockNotice({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
    // The parent server-start surface owns polling. The notice only renders the
    // shared state so nesting it cannot accidentally create another poller.
    const { blocked, message } = useContext(ServerBlockContext);
    if (!blocked) return null;
    return (
        <div
            className={className}
            style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)',
                borderRadius: 10, padding: '12px 14px', color: '#fca5a5', fontSize: 13, lineHeight: 1.5,
                ...style,
            }}
        >
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1, color: '#f87171' }} />
            <span>{message}</span>
        </div>
    );
}
